import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Lock,
  ArrowRight,
  RotateCcw,
  Paperclip,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { COUNTRIES } from "@/lib/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPOS = [
  "Paquete no cargado a sistema de casillero",
  "Problema con guía ya procesada y despachada",
];

const EMPTY = {
  nombre: "",
  casillero: "",
  email: "",
  whatsapp: "",
  tipo_reclamo: "",
  tracking: "",
  detalle: "",
};

const fieldBase =
  "w-full h-12 bg-paper border border-border px-4 text-ink font-sans text-base rounded-sm outline-none transition-colors duration-200 focus:border-ink focus:bg-white placeholder:text-muted-foreground/70";

function Field({ label, hint, error, children, testid }) {
  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      <label className="font-sans text-xs font-semibold uppercase tracking-[0.14em] text-ink/70">
        {label}
      </label>
      {children}
      {hint && !error && (
        <span className="text-xs text-muted-foreground">{hint}</span>
      )}
      {error && (
        <span className="flex items-center gap-1.5 text-xs font-medium text-alert" data-testid={`${testid}-error`}>
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </span>
      )}
    </div>
  );
}

export default function ClaimForm({ config }) {
  const cfg = config || COUNTRIES.venezuela;
  const prefix = cfg.casilleroPrefix;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | blocked
  const [successTracking, setSuccessTracking] = useState("");
  const [successNumero, setSuccessNumero] = useState("");
  const [file, setFile] = useState(null);

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Ingresa tu nombre y apellido";
    if (!form.casillero.trim()) e.casillero = "Ingresa tu código de casillero";
    else if (!form.casillero.trim().toUpperCase().startsWith(prefix))
      e.casillero = `El código debe comenzar con ${prefix}`;
    if (!form.email.trim()) e.email = "Ingresa tu correo";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Correo no válido";
    if (!form.whatsapp.trim()) e.whatsapp = "Ingresa tu número de WhatsApp";
    if (!form.tipo_reclamo) e.tipo_reclamo = "Selecciona el tipo de reclamo";
    if (!form.tracking.trim()) e.tracking = "Ingresa el número de guía/tracking";
    if (!form.detalle.trim()) e.detalle = "Describe tu reclamo";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      let screenshot_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        screenshot_path = up.data.path;
      }
      const res = await axios.post(`${API}/claims`, {
        ...form,
        pais: cfg.pais,
        tracking: form.tracking.trim().toUpperCase(),
        screenshot_path,
      });
      setSuccessTracking(res.data.tracking);
      setSuccessNumero(res.data.numero_reclamo || "");
      setStatus("success");
    } catch (err) {
      if (err?.response?.status === 409) {
        setSuccessTracking(form.tracking.trim().toUpperCase());
        setStatus("blocked");
      } else {
        setStatus("idle");
        toast.error(
          err?.response?.data?.detail ||
            "No se pudo enviar el reclamo. Intenta nuevamente."
        );
      }
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setErrors({});
    setSuccessTracking("");
    setSuccessNumero("");
    setFile(null);
    setStatus("idle");
  };

  return (
    <div
      id="reclamo"
      data-testid="claim-form-card"
      className="relative bg-white border border-ink/10 rounded-md shadow-[0_24px_80px_-24px_rgba(7,30,34,0.35)] p-7 sm:p-10 lg:p-12 overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1.5 w-full bg-gold" />

      <AnimatePresence mode="wait">
        {status === "success" && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-start gap-5 py-6"
            data-testid="claim-success-state"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold">
              <CheckCircle2 className="h-9 w-9 text-ink" strokeWidth={2.2} />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
              Reclamo recibido
            </h3>
            <p className="text-base text-muted-foreground max-w-md">
              Registramos tu reclamo para el tracking{" "}
              <span className="font-mono font-medium text-ink">{successTracking}</span>.
              Nuestro equipo iniciará la búsqueda de tu paquete y te contactará por correo electrónico.
            </p>
            {successNumero && (
              <div className="mt-1 w-full rounded-sm border-2 border-gold bg-gold/10 p-4" data-testid="success-numero-reclamo">
                <p className="font-mono text-xs uppercase tracking-widest text-ink/60">
                  Tu número de reclamo
                </p>
                <p className="font-display text-2xl sm:text-3xl font-extrabold text-ink mt-1 tracking-tight">
                  {successNumero}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Guarda este número para dar seguimiento a tu reclamo.
                </p>
              </div>
            )}
            <div className="mt-1 w-full rounded-sm border border-border bg-paper p-4">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Estado
              </p>
              <p className="font-sans text-sm font-semibold text-ink mt-1">
                En proceso · Flujo automatizado activado
              </p>
            </div>
            <button
              onClick={reset}
              data-testid="new-claim-button"
              className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-ink underline underline-offset-4 decoration-gold decoration-2 hover:text-ink/70 transition-colors"
            >
              <RotateCcw className="h-4 w-4" /> Registrar otro tracking
            </button>
          </motion.div>
        )}

        {status === "blocked" && (
          <motion.div
            key="blocked"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-start gap-5 py-6"
            data-testid="claim-blocked-state"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-alert/10 border border-alert/30">
              <Lock className="h-8 w-8 text-alert" strokeWidth={2.2} />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-alert tracking-tight">
              Tracking ya reclamado
            </h3>
            <p className="text-base text-muted-foreground max-w-md" aria-live="polite">
              El número de guía{" "}
              <span className="font-mono font-medium text-ink">{successTracking}</span>{" "}
              ya tiene un reclamo registrado en nuestro sistema. No es posible enviar un
              reclamo repetido para el mismo tracking.
            </p>
            <div className="mt-2 w-full rounded-sm border border-alert/30 bg-alert/5 p-4">
              <p className="font-sans text-sm text-ink">
                Si necesitas seguimiento sobre este reclamo, contáctanos por WhatsApp e
                indícanos tu número de guía.
              </p>
            </div>
            <button
              onClick={reset}
              data-testid="try-another-button"
              className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-ink underline underline-offset-4 decoration-alert decoration-2 hover:text-ink/70 transition-colors"
            >
              <RotateCcw className="h-4 w-4" /> Probar con otro tracking
            </button>
          </motion.div>
        )}

        {(status === "idle" || status === "submitting") && (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onSubmit={onSubmit}
            className="flex flex-col gap-6"
            noValidate
            data-testid="claim-form"
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-xs text-gold">●</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Formulario de reclamo por tracking
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nombre y Apellido" error={errors.nombre} testid="field-nombre">
                <input
                  className={fieldBase}
                  value={form.nombre}
                  onChange={set("nombre")}
                  placeholder="Juan Pérez"
                  data-testid="input-nombre"
                />
              </Field>

              <Field
                label="Código del casillero"
                hint={`Comienza con ${prefix} seguido de varios dígitos`}
                error={errors.casillero}
                testid="field-casillero"
              >
                <input
                  className={`${fieldBase} font-mono uppercase`}
                  value={form.casillero}
                  onChange={set("casillero")}
                  placeholder={cfg.casilleroPlaceholder}
                  data-testid="input-casillero"
                />
              </Field>

              <Field label="Correo electrónico" error={errors.email} testid="field-email">
                <input
                  type="email"
                  className={fieldBase}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="correo@ejemplo.com"
                  data-testid="input-email"
                />
              </Field>

              <Field label="Número de WhatsApp" error={errors.whatsapp} testid="field-whatsapp">
                <input
                  className={fieldBase}
                  value={form.whatsapp}
                  onChange={set("whatsapp")}
                  placeholder="+58 412 000 0000"
                  data-testid="input-whatsapp"
                />
              </Field>
            </div>

            <Field label="Tipo de reclamo" error={errors.tipo_reclamo} testid="field-tipo">
              <Select value={form.tipo_reclamo} onValueChange={set("tipo_reclamo")}>
                <SelectTrigger
                  className="w-full h-12 bg-paper border border-border rounded-sm text-base data-[placeholder]:text-muted-foreground/70 focus:border-ink"
                  data-testid="select-tipo"
                >
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`tipo-option-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Número de Guía / Tracking"
              hint="Cada tracking solo puede reclamarse una vez"
              error={errors.tracking}
              testid="field-tracking"
            >
              <input
                className={`${fieldBase} font-mono uppercase`}
                value={form.tracking}
                onChange={set("tracking")}
                placeholder="Ej. 1Z999AA10123456784"
                data-testid="input-tracking"
              />
            </Field>

            <Field label="Detalle del reclamo" error={errors.detalle} testid="field-detalle">
              <textarea
                className={`${fieldBase} h-auto min-h-[120px] py-3 resize-y`}
                value={form.detalle}
                onChange={set("detalle")}
                placeholder="Describe qué ocurrió con tu paquete o envío…"
                data-testid="input-detalle"
              />
            </Field>

            <div className="flex flex-col gap-2" data-testid="field-screenshot">
              <label className="font-sans text-xs font-semibold uppercase tracking-[0.14em] text-ink/70">
                Adjuntar screenshot / factura{" "}
                <span className="normal-case tracking-normal text-muted-foreground">(opcional)</span>
              </label>
              {!file ? (
                <label
                  className="flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-ink/25 bg-paper px-4 py-4 transition-colors duration-200 hover:border-ink hover:bg-white"
                  data-testid="screenshot-dropzone"
                >
                  <Paperclip className="h-5 w-5 text-ink/60" />
                  <span className="text-sm text-muted-foreground">
                    Sube una imagen del producto, orden de compra o factura (JPG, PNG, WEBP, PDF · máx 10MB)
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
                    className="hidden"
                    data-testid="input-screenshot"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              ) : (
                <div
                  className="flex items-center justify-between gap-3 rounded-sm border border-ink/20 bg-white px-4 py-3"
                  data-testid="screenshot-selected"
                >
                  <span className="flex items-center gap-2 text-sm text-ink truncate">
                    <Paperclip className="h-4 w-4 text-gold shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    data-testid="remove-screenshot"
                    className="text-muted-foreground hover:text-alert transition-colors"
                    aria-label="Quitar archivo"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              data-testid="submit-claim-button"
              className="group mt-2 inline-flex h-14 items-center justify-center gap-3 rounded-sm bg-gold px-8 font-sans text-base font-semibold text-ink shadow-[0_10px_30px_-8px_rgba(242,201,76,0.6)] transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-1 hover:bg-[#f5d268] disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {status === "submitting" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Enviando reclamo…
                </>
              ) : (
                <>
                  Enviar reclamo
                  <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="text-xs text-muted-foreground">
              Al enviar, tus datos se registran y se envían de forma segura a nuestro flujo de
              atención automatizado.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
